<script lang="ts">
    import cn from 'classnames';

    import { CustomIcon, getIcon } from '@components/icons';
    import { globalStore } from '@components/stores';
    import { LL } from '@i18n/i18n-svelte';
    import rtaLogo from '@images/rta_logo.svg';
    import { APP_NAME } from '@shared/constants';
    import { SUPPORT_EMAIL } from '@shared/emails';
    import { RTA_LINK } from '@shared/links';
    import { URLS } from '@shared/routing';
    import { Button } from '@uikit/button';
    import { Font, FontTypes } from '@uikit/font';
    import { Link, LinkTypes } from '@uikit/link';
    import { Size, Target } from '@uikit/types';

    import s from './footer.module.css';

    export let isOperator: boolean;

    const currentYear = new Date().getFullYear();

    function changeSiteVersion() {
        if ($globalStore.isDesktopSite) {
            globalStore.switchToDesktop(false);
        } else {
            globalStore.switchToDesktop(true);
        }
    }
</script>

<footer class={cn(s.root, { [s.desktop]: $globalStore.isDesktopSite })}>
    <div class={s.container}>
        <nav class={s.leftCol}>
            <div class={cn(s.innerRow, s.fluidInnerCol)}>
                <ul class={cn(s.strongInnerCol, s.menuList)}>
                    <Font tagName={FontTypes.TagName.li} className={s.menuItem}>
                        <Link
                            href={URLS.privacyPolicy()}
                            attrs={{ 'data-testid': 'privacyPolicyLink' }}
                            color={LinkTypes.Color.Secondary}
                        >
                            {$LL.footer.menu.privacyPolicyLabel()}
                        </Link>
                    </Font>
                    <Font tagName={FontTypes.TagName.li} className={s.menuItem}>
                        <Link
                            href={URLS.termsOfUse()}
                            attrs={{ 'data-testid': 'termsOfUseLink' }}
                            color={LinkTypes.Color.Secondary}
                        >
                            {$LL.footer.menu.termsOfUseLabel()}
                        </Link>
                    </Font>
                    {#if isOperator}
                        <Font tagName={FontTypes.TagName.li} className={s.menuItem}>
                            <Link
                                    href={URLS.chatRules()}
                                    attrs={{ 'data-testid': 'chatRules' }}
                                    color={LinkTypes.Color.Secondary}
                            >
                                {$LL.footer.menu.chatRules()}
                            </Link>
                        </Font>
                    {/if}
                </ul>
                <ul class={cn(s.strongInnerCol, s.menuList)}>
                    <Font
                        tagName={FontTypes.TagName.li}
                        type={FontTypes.Type.body}
                        color={FontTypes.Color.Secondary}
                        className={s.menuItem}
                    >
                        <Link
                            color={LinkTypes.Color.Secondary}
                            target={Target.blank}
                            href={URLS.talkToStrangers()}
                        >
                            {$LL.footer.talkToStrangers()}
                        </Link>
                    </Font>
                    <Font
                        tagName={FontTypes.TagName.li}
                        type={FontTypes.Type.body}
                        color={FontTypes.Color.Secondary}
                        className={s.menuItem}
                    >
                        <Link
                            color={LinkTypes.Color.Secondary}
                            target={Target.blank}
                            href={URLS.reportContent()}
                            attrs={{ 'data-testid': 'reportContentLink' }}
                        >
                            {$LL.reportContent.title()}
                        </Link>
                    </Font>
                </ul>
            </div>
            <ul class={cn(s.fluidInnerCol, s.menuList)}>
                <Font
                    tagName={FontTypes.TagName.li}
                    type={FontTypes.Type.body}
                    color={FontTypes.Color.Secondary}
                    className={s.menuItem}
                >
                    <Link
                        color={LinkTypes.Color.Secondary}
                        target={Target.blank}
                        href={URLS.sitemap()}
                    >
                        {$LL.footer.sitemap()}
                    </Link>
                </Font>
            </ul>
        </nav>

        <div class={s.rightCol}>
            <div class={cn(s.strongInnerCol, s.siteVersionCol)}>
                <Button
                    classes={cn(s.versionButton)}
                    icon={$globalStore.isDesktopSite ? getIcon(CustomIcon.phone) : undefined}
                    size={Size.Small}
                    on:click={changeSiteVersion}
                >
                    {$globalStore.isDesktopSite ? $LL.footer.buttonVersionMobile() : $LL.footer.buttonVersionDesktop()}
                </Button>
                <Font
                    tagName={FontTypes.TagName.div}
                    type={FontTypes.Type.body}
                    color={FontTypes.Color.Secondary}
                    className={s.supportLink}
                >
                    {$LL.footer.menu.contactUsLabel()}:
                    <Link
                        href={`mailto:${SUPPORT_EMAIL}`}
                        classes={s.menuItem}
                        attrs={{ 'data-testid': 'contactUsLink' }}
                        color={LinkTypes.Color.Secondary}
                    >
                        {SUPPORT_EMAIL}
                    </Link>
                </Font>
            </div>
            <div class={s.strongInnerCol}>
                <div class={s.complianceWrapper}>
                    <Link
                        classes={s.rtaLogo}
                        href={RTA_LINK}
                        target={Target.blank}
                        attrs={{ rel: 'noopener' }}
                    >
                        <img src={rtaLogo} alt="RTA" />
                    </Link>
                    <Font
                        tagName={FontTypes.TagName.p}
                        type={FontTypes.Type.tiny}
                        className={cn(s.compliance, s.complianceContainer)}
                    >
                        <Link href={URLS.statement2257()}>
                            <Font
                                tagName={FontTypes.TagName.p}
                                type={$globalStore.isDesktopSite ? FontTypes.Type.caption : FontTypes.Type.tiny}
                                className={cn(s.compliance)}
                                attrs={{ 'data-testid': 'statement2257Link' }}
                            >
                                {$LL.footer.complianceLabel()}
                            </Font>
                        </Link>
                    </Font>
                </div>
            </div>
        </div>
    </div>
    <div class={cn(s.container, s.addressSection)}>
        <div class={s.companyAddress}></div>
        <Font
            tagName={FontTypes.TagName.p}
            type={$globalStore.isDesktopSite ? FontTypes.Type.caption : FontTypes.Type.tiny}
            className={s.copyright}
        >
            {$LL.footer.copyrightLabel({ year: currentYear, appName: APP_NAME })}
        </Font>
    </div>
</footer>
